/** Flat catalog of one namespace: semantic key -> ICU message. */
export type Messages = Readonly<Record<string, string>>;

/** namespace -> messages */
export type NamespaceBundles = Readonly<Record<string, Messages>>;

/** Build-time English fallback compiled into a client (embedded-catalog.schema.json). */
export interface EmbeddedCatalog {
  readonly schema_version: 1;
  readonly catalog_revision: string;
  readonly client_id: string;
  readonly contract_version: number;
  readonly contract_sha256: string;
  readonly language: string;
  readonly namespaces: NamespaceBundles;
}

export interface ArtifactEntry {
  readonly path: string;
  readonly sha256: string;
  readonly bytes: number;
}

/** Release manifest served at /l10n/<client>/v<k>/manifest.json (manifest.schema.json). */
export interface Manifest {
  readonly schema_version: 1;
  readonly catalog_revision: string;
  readonly min_client_contract: number;
  readonly verified_contracts: Readonly<Record<string, readonly number[]>>;
  readonly contract_snapshots: Readonly<Record<string, Readonly<Record<string, string>>>>;
  readonly base_language: string;
  readonly languages: readonly string[];
  readonly fallback: Readonly<Record<string, readonly string[]>>;
  readonly clients: Readonly<Record<string, Readonly<Record<string, Readonly<Record<string, ArtifactEntry>>>>>>;
}

/** What this build of a client is: its id, contract and declared namespaces, taken from its embedded catalog. */
export interface ClientIdentity {
  readonly clientId: string;
  readonly contractVersion: number;
  readonly contractSha256: string;
  readonly baseLanguage: string;
  readonly namespaces: readonly string[];
  /** Revision of the embedded catalog; a cache written by another build of the client is not reused. */
  readonly embeddedRevision: string;
}

/** One complete, verified catalog revision for one language and its fallback chain. */
export interface CatalogSet {
  readonly revision: string;
  readonly language: string;
  /** Requested language first, ending in the base language. */
  readonly chain: readonly string[];
  readonly baseLanguage: string;
  /** Published languages of the release and their fallback chains. */
  readonly languages: readonly string[];
  readonly fallback: Readonly<Record<string, readonly string[]>>;
  /** language -> namespace -> messages, only the client's declared namespaces. */
  readonly bundles: Readonly<Record<string, NamespaceBundles>>;
  /** Verified artifact bytes, kept so a cached copy can be re-verified. */
  readonly artifacts: readonly VerifiedArtifact[];
}

export interface VerifiedArtifact {
  readonly language: string;
  readonly namespace: string;
  readonly entry: ArtifactEntry;
  readonly text: string;
}

export type CatalogErrorCode =
  | "network"
  | "timeout"
  | "aborted"
  | "http_status"
  | "content_type"
  | "too_large"
  | "integrity"
  | "manifest_invalid"
  | "artifact_invalid"
  | "contract_incompatible"
  | "language_unavailable"
  | "url_rejected";

export class CatalogError extends Error {
  readonly code: CatalogErrorCode;
  /** HTTP status of an http_status failure; null otherwise. */
  readonly status: number | null;

  constructor(code: CatalogErrorCode, detail: string, status: number | null = null) {
    super(`${code}: ${detail}`);
    this.name = "CatalogError";
    this.code = code;
    this.status = status;
  }
}

/** Minimal Web Crypto surface the loader needs; injectable for tests. */
export interface DigestProvider {
  readonly subtle: { digest(algorithm: "SHA-256", data: ArrayBuffer): Promise<ArrayBuffer> };
}

/** Minimal Storage surface of the catalog cache; injectable for tests. */
export interface CatalogStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;
